"use client"

import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { register } from "../redux/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { EyeClosed, EyeOff, Check, X } from "lucide-react"
import PhoneInput from "react-phone-input-2";
import { validatePassword, getPasswordChecks, getPasswordStrength } from "../utils/passwordValidation";
import { generateRecaptchaToken } from "../utils/recaptcha";

function RegisterForm() {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [phone, setPhone] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [passwordFocused, setPasswordFocused] = useState(false)
  const authError = useSelector((state) => state.auth.error);

  // Use utility functions for password validation
  const passwordChecks = getPasswordChecks(password, name);
  const passwordStrength = getPasswordStrength(password, name);

  console.log(authError);

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    const passwordError = validatePassword(password, name);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    if (!acceptTerms) {
      setError("Please accept the terms and conditions")
      return
    }

    setIsLoading(true)

    try {
      // Generate reCAPTCHA v3 token
      const recaptchaToken = await generateRecaptchaToken('register');

      const result = await dispatch(register({ 
        name, 
        email, 
        password, 
        phone, 
        recaptchaToken 
      }));

      if (register.fulfilled.match(result)) {
        navigate(`/verify-otp?email=${email}`, { replace: true })
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="auth-card">
        <div className="auth-header">
          <h2 className="auth-title">Quickly Create Kaxaa Account</h2>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {(error || authError) && <div className="error-alert">{error || authError}</div>}

          <div className="form-group m-0">
            <label className="required" htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input form-control w-full"
            />
          </div>

          <div className="form-group m-0">
            <label className="required" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="form-input form-control w-full"
            />
          </div>

          <div className="form-group m-0">
            <label className="required" htmlFor="phone">Phone</label>
            {/* <input
            id="email"
            type="phone"
            placeholder="Enter your phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            className="form-input form-control w-full"
          /> */}
            <PhoneInput
              country={"us"}   // default Pakistan flag
              value={phone}
              onChange={(e) => setPhone(e)}
              inputClass="form-input form-control w-100"
            />
          </div>

          <div className="form-group m-0">
            <label className="required" htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                required
                className="form-input form-control w-full"
              />
              <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
              </button>
            </div>

            {/* Password Requirements */}
            {(passwordFocused || password.length > 0) && (
              <div className="password-requirements">
                <div className="requirements-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="requirements-title mt-1">Password must contain:</span>
                    {passwordStrength && (
                      <span
                        className="password-strength"
                        style={{ color: passwordStrength.color, fontWeight: '500' }}
                      >
                        {passwordStrength.text}
                      </span>
                    )}
                  </div>
                </div>
                <div className="requirements-list">
                  <div className={`requirement-item ${passwordChecks.length ? 'valid' : 'invalid'}`}>
                    {passwordChecks.length ? <Check size={14} /> : <X size={14} />}
                    <span>At least 8 characters</span>
                  </div>
                  <div className={`requirement-item ${passwordChecks.uppercase ? 'valid' : 'invalid'}`}>
                    {passwordChecks.uppercase ? <Check size={14} /> : <X size={14} />}
                    <span>One uppercase letter (A-Z)</span>
                  </div>
                  <div className={`requirement-item ${passwordChecks.lowercase ? 'valid' : 'invalid'}`}>
                    {passwordChecks.lowercase ? <Check size={14} /> : <X size={14} />}
                    <span>One lowercase letter (a-z)</span>
                  </div>
                  <div className={`requirement-item ${passwordChecks.number ? 'valid' : 'invalid'}`}>
                    {passwordChecks.number ? <Check size={14} /> : <X size={14} />}
                    <span>One number (0-9)</span>
                  </div>
                  <div className={`requirement-item ${passwordChecks.special ? 'valid' : 'invalid'}`}>
                    {passwordChecks.special ? <Check size={14} /> : <X size={14} />}
                    <span>One special character (@#$%^&*)</span>
                  </div>
                  {name && (
                    <div className={`requirement-item ${passwordChecks.noName ? 'valid' : 'invalid'}`}>
                      {passwordChecks.noName ? <Check size={14} /> : <X size={14} />}
                      <span>Should not contain your name</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-group m-0">
            <label className="required" htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="form-input form-control w-full"
              />
              <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? <EyeClosed size={'18'} /> : <EyeOff size={'18'} />}
              </button>
            </div>
            {/* Password Match Indicator */}
            {confirmPassword.length > 0 && (
              <div className={`requirement-item ${password === confirmPassword ? 'valid' : 'invalid'}`} style={{ marginTop: '8px' }}>
                {password === confirmPassword ? <Check size={14} /> : <X size={14} />}
                <span>{password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
              </div>
            )}
          </div>

          <div className="checkbox-group">
            <input
              id="terms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="checkbox"
            />
            <label htmlFor="terms" className="checkbox-label text-secondary">
              By creating an account you indicate you have read and agree to the <Link className="link-button" to="https://kaxaa.com/terms-condition/" target="_blank" rel="noreferrer"> Terms &amp; Conditions </Link> <span className="px-1">and</span> <Link className="link-button" to="https://kaxaa.com/privacy-policy/" target="_blank" rel="noreferrer"> Privacy Policy </Link>
            </label>
          </div>

          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? (
              <div className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating Account...
              </div>
            ) : (
              "Create Account"
            )}
          </button>

        </form>
      </div>
      <div className="auth-switch mt-3">
        Already have an account?{" "}
        <button type="button" className="link-button" onClick={() => navigate("/login")}>
          Sign in
        </button>
      </div>
    </>
  )
}

export default RegisterForm
